import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchIssuePoRegister } from "../api/mrrInventoryApi";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import type { MilestoneItemKind, MilestoneTemplate } from "../types";
import { sortConstructionPhases } from "../utils/constructionTimeline";
import { sortStandardResources } from "../utils/standardResources";

const KIND_OPTIONS: { value: MilestoneItemKind; label: string }[] = [
  { value: "material", label: "Material / supplies" },
  { value: "subcontractor_bill", label: "Subcontractor bill" },
  { value: "govt_bill", label: "Government bill / challan" },
  { value: "work_done_bill", label: "Work-done bill (dossier)" },
];

function MilestoneTimelineMultiLinkField({
  idPrefix,
  selectedIds,
  onChange,
  disabled,
}: {
  idPrefix: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const { constructionTimelinePhases } = useAppState();
  const sorted = useMemo(
    () => sortConstructionPhases(constructionTimelinePhases),
    [constructionTimelinePhases],
  );
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  return (
    <div className="field">
      <div className="milestone-timeline-multi__label">Construction timeline levels (optional)</div>
      <p className="text-muted section-lead section-lead--tight" style={{ margin: "0.15rem 0 0.5rem" }}>
        Select one or more Gantt rows this milestone relates to (reference only). Levels are defined under{" "}
        <Link to="/construction-timeline-master">Timeline master</Link>.
      </p>
      {sorted.length === 0 ? (
        <p className="text-muted section-lead section-lead--tight">No timeline levels in master yet.</p>
      ) : (
        <>
          <div
            className="milestone-timeline-check-grid"
            role="group"
            aria-label="Linked construction timeline levels"
          >
            {sorted.map((p) => (
              <label key={p.id} className="milestone-timeline-check">
                <input
                  type="checkbox"
                  id={`${idPrefix}-${p.id}`}
                  checked={selected.has(p.id)}
                  onChange={() => toggle(p.id)}
                  disabled={disabled}
                />
                <span className="milestone-timeline-check__text">
                  <span className="milestone-timeline-check__title">{p.title}</span>
                  <span className="milestone-timeline-check__meta">~{p.approxDays} d plan</span>
                </span>
              </label>
            ))}
          </div>
          <button
            type="button"
            className="btn btn--link btn--sm"
            style={{ marginTop: "0.35rem" }}
            onClick={() => onChange([])}
            disabled={disabled}
          >
            Clear all timeline links
          </button>
        </>
      )}
    </div>
  );
}

function MilestoneResourceMultiSelectField({
  idPrefix,
  selectedIds,
  onChange,
  disabled,
}: {
  idPrefix: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}) {
  const { standardResources } = useAppState();
  const sorted = useMemo(() => sortStandardResources(standardResources), [standardResources]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  return (
    <div className="field">
      <div className="milestone-timeline-multi__label">Standard resources (optional)</div>
      <p className="text-muted section-lead section-lead--tight" style={{ margin: "0.15rem 0 0.5rem" }}>
        Select resource categories linked to this milestone (informational). Item lines and quantities are defined
        under <Link to="/standard-resources-master">Standard resources master</Link>.
      </p>
      {sorted.length === 0 ? (
        <p className="text-muted section-lead section-lead--tight">
          No resources in master yet — add them under{" "}
          <Link to="/standard-resources-master">Standard resources master</Link>.
        </p>
      ) : (
        <div
          className="milestone-timeline-check-grid"
          role="group"
          aria-label="Linked standard resources"
        >
          {sorted.map((r) => (
            <label key={r.id} className="milestone-timeline-check">
              <input
                type="checkbox"
                id={`${idPrefix}-${r.id}`}
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
                disabled={disabled}
              />
              <span className="milestone-timeline-check__text milestone-timeline-check__text--single">
                <span className="milestone-timeline-check__title">{r.title}</span>
              </span>
            </label>
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn btn--link btn--sm"
        style={{ marginTop: "0.35rem" }}
        onClick={() => onChange([])}
        disabled={disabled}
      >
        Clear all resource links
      </button>
    </div>
  );
}

function MilestoneTemplateSummaryCard({
  template: t,
  phaseIndex,
  isActive,
  onOpen,
}: {
  template: MilestoneTemplate;
  phaseIndex: number;
  isActive: boolean;
  onOpen: () => void;
}) {
  const itemCount = t.items.length;
  const resCount = t.linkedResourceIds?.length ?? 0;
  const tlCount = t.linkedConstructionTimelinePhaseIds?.length ?? 0;
  const note = t.notes.trim();
  return (
    <button
      type="button"
      className={`milestone-master-picker-card${isActive ? " milestone-master-picker-card--active" : ""}`}
      onClick={onOpen}
      aria-haspopup="dialog"
      aria-expanded={isActive}
    >
      <div className="milestone-master-picker-card__content">
        <span className="milestone-master-picker-card__top">
          <span className="milestone-master-picker-card__phase-badge">Phase {phaseIndex + 1}</span>
        </span>
        <span className="milestone-master-picker-card__title">{t.title}</span>
      </div>
      <span className="milestone-master-picker-card__footer">
        <span>{isActive ? "Editor open" : "Click card to edit"}</span>
      </span>
    </button>
  );
}

function TemplateEditorCard({
  template: t,
  embedInModal,
  onDeleted,
}: {
  template: MilestoneTemplate;
  embedInModal?: boolean;
  onDeleted?: () => void;
}) {
  const {
    updateMilestoneTemplate,
    removeMilestoneTemplate,
    addMilestoneTemplateItem,
    removeMilestoneTemplateItem,
  } = useAppState();
  const [title, setTitle] = useState(t.title);
  const [notes, setNotes] = useState(t.notes);
  const [linkedTimelineIds, setLinkedTimelineIds] = useState<string[]>(() => [...(t.linkedConstructionTimelinePhaseIds ?? [])]);
  const [linkedResourceIds, setLinkedResourceIds] = useState<string[]>(() => [...(t.linkedResourceIds ?? [])]);
  const [newKind, setNewKind] = useState<MilestoneItemKind>("material");
  const [newLabel, setNewLabel] = useState("");
  const [isLocked, setIsLocked] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    void fetchIssuePoRegister({ limit: 1, milestoneTemplateId: t.id }).then((pos) => {
      if (active) setIsLocked(pos.length > 0);
    }).catch(() => {
      if (active) setIsLocked(false);
    });
    return () => { active = false; };
  }, [t.id]);

  useEffect(() => {
    setTitle(t.title);
    setNotes(t.notes);
    setLinkedTimelineIds([...(t.linkedConstructionTimelinePhaseIds ?? [])]);
    setLinkedResourceIds([...(t.linkedResourceIds ?? [])]);
  }, [t.id, t.title, t.notes, t.linkedConstructionTimelinePhaseIds, t.linkedResourceIds]);

  return (
    <div
      className={embedInModal ? "milestone-master-modal-editor" : "card card--gov card--elevated"}
      role="region"
      aria-label={`Edit milestone: ${t.title}`}
    >
      <div className="toolbar-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <h2 className="card__title card__title--gov" style={{ margin: 0 }}>
          {t.title}
        </h2>
        {isLocked !== true && (
          <button
            type="button"
            className="btn btn--tg-secondary btn--sm milestone-row__remove"
            onClick={() => {
              if (window.confirm(`Remove milestone “${t.title}” from master? Progress on beneficiary files will lose this phase.`)) {
                void removeMilestoneTemplate(t.id).then((ok) => {
                  if (ok) onDeleted?.();
                });
              }
            }}
          >
            Delete template
          </button>
        )}
      </div>

      {isLocked === true && (
        <div className="help-banner help-banner--warning" style={{ marginBottom: "1rem" }}>
          <strong>Locked for editing</strong>
          <p style={{ margin: "0.25rem 0 0" }}>
            Purchase Orders have already been generated using this milestone. To preserve data consistency, this master blueprint cannot be edited or deleted. It is view-only.
          </p>
        </div>
      )}

      <div className="field">
        <label htmlFor={`mt-title-${t.id}`}>Title (shown on every beneficiary file)</label>
        <input id={`mt-title-${t.id}`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} disabled={isLocked === true} />
      </div>
      <div className="field">
        <label htmlFor={`mt-notes-${t.id}`}>Notes for officers</label>
        <textarea id={`mt-notes-${t.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} disabled={isLocked === true} />
      </div>
      <MilestoneTimelineMultiLinkField
        idPrefix={`mt-timeline-${t.id}`}
        selectedIds={linkedTimelineIds}
        onChange={setLinkedTimelineIds}
        disabled={isLocked === true}
      />
      <MilestoneResourceMultiSelectField
        idPrefix={`mt-res-${t.id}`}
        selectedIds={linkedResourceIds}
        onChange={setLinkedResourceIds}
        disabled={isLocked === true}
      />
      {isLocked !== true && (
        <button
          type="button"
          className="btn btn--primary btn--tg btn--sm"
          onClick={() =>
            void updateMilestoneTemplate(t.id, {
              title,
              notes,
              linkedConstructionTimelinePhaseIds: linkedTimelineIds,
              linkedResourceIds,
            })
          }
        >
          Save title, notes, timeline &amp; resource links
        </button>
      )}

    </div>
  );
}

export function MilestoneMaster() {
  const { milestoneTemplates, addMilestoneTemplate } = useAppState();
  const sorted = useMemo(
    () => [...milestoneTemplates].sort((a, b) => a.sortOrder - b.sortOrder),
    [milestoneTemplates]
  );

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalTemplateId, setEditModalTemplateId] = useState<string | null>(null);
  const [createTitle, setCreateTitle] = useState("");
  const [createNotes, setCreateNotes] = useState("");
  const [createLinkedTimelineIds, setCreateLinkedTimelineIds] = useState<string[]>([]);
  const [createLinkedResourceIds, setCreateLinkedResourceIds] = useState<string[]>([]);

  const editModalTemplate = useMemo(
    () => (editModalTemplateId ? sorted.find((x) => x.id === editModalTemplateId) : undefined),
    [editModalTemplateId, sorted],
  );

  useEffect(() => {
    if (editModalTemplateId && !editModalTemplate) setEditModalTemplateId(null);
  }, [editModalTemplateId, editModalTemplate]);

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setCreateTitle("");
    setCreateNotes("");
    setCreateLinkedTimelineIds([]);
    setCreateLinkedResourceIds([]);
  }, []);

  useEffect(() => {
    if (!createModalOpen && !editModalTemplateId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeCreateModal();
        setEditModalTemplateId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [createModalOpen, editModalTemplateId, closeCreateModal]);

  const openCreateModal = () => {
    setEditModalTemplateId(null);
    setCreateModalOpen(true);
  };

  const openEditModal = (id: string) => {
    setCreateModalOpen(false);
    setEditModalTemplateId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="milestone-master-page">
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Milestone master" }]}
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            Milestone master
            <span className="milestone-master__catalog-count">{sorted.length}</span>
          </span>
        }
        actions={
          <button type="button" className="btn btn--primary btn--tg btn--sm" onClick={openCreateModal}>
            Add milestone
          </button>
        }
      />

      <section
        className="milestone-master__catalog"
        aria-labelledby="milestone-master-catalog-heading"
        style={{ marginTop: "1.75rem" }}
      >


        {sorted.length === 0 ? (
          <div className="milestone-master__empty">
            <p className="milestone-master__empty-title">No phases yet</p>
            <p className="milestone-master__empty-desc">
              Create your first milestone template with <strong>Add milestone</strong>. You can add checklist items
              after the phase exists.
            </p>
          </div>
        ) : (
          <div className="milestone-master-picker-grid">
            {sorted.map((t, i) => (
              <MilestoneTemplateSummaryCard
                key={t.id}
                template={t}
                phaseIndex={i}
                isActive={editModalTemplateId === t.id}
                onOpen={() => openEditModal(t.id)}
              />
            ))}
          </div>
        )}
      </section>

      {createModalOpen ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            closeCreateModal();
          }}
        >
          <div
            className="modal milestone-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="milestone-master-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="milestone-master-create-title" className="milestone-master-modal__title">
                Add milestone
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={closeCreateModal}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <div className="field">
                <label htmlFor="new-mt-title">Phase title</label>
                <input
                  id="new-mt-title"
                  value={createTitle}
                  onChange={(e) => setCreateTitle(e.target.value)}
                  placeholder="e.g. Phase 4 — Electrification"
                  maxLength={200}
                />
              </div>
              <div className="field">
                <label htmlFor="new-mt-notes">Notes (optional)</label>
                <textarea
                  id="new-mt-notes"
                  rows={2}
                  value={createNotes}
                  onChange={(e) => setCreateNotes(e.target.value)}
                  maxLength={2000}
                />
              </div>
              <MilestoneTimelineMultiLinkField
                idPrefix="new-mt-timeline"
                selectedIds={createLinkedTimelineIds}
                onChange={setCreateLinkedTimelineIds}
              />
              <MilestoneResourceMultiSelectField
                idPrefix="new-mt-res"
                selectedIds={createLinkedResourceIds}
                onChange={setCreateLinkedResourceIds}
              />
              <p className="text-muted section-lead section-lead--tight" style={{ marginTop: "0.25rem" }}>
                After you create the template, open its card and use <strong>Required items &amp; bills</strong> in the
                dialog to add checklist rows (type + description).
              </p>
              <div className="toolbar-row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.75rem" }}>
                <button
                  type="button"
                  className="btn btn--primary btn--tg"
                  disabled={!createTitle.trim()}
                  onClick={() => {
                    void (async () => {
                      const ok = await addMilestoneTemplate({
                        title: createTitle.trim(),
                        notes: createNotes.trim(),
                        items: [],
                        linkedConstructionTimelinePhaseIds: createLinkedTimelineIds,
                        linkedResourceIds: createLinkedResourceIds,
                      });
                      if (!ok) return;
                      closeCreateModal();
                    })();
                  }}
                >
                  Create milestone template
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editModalTemplate ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            setEditModalTemplateId(null);
          }}
        >
          <div
            className="modal milestone-master-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="milestone-master-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="milestone-master-modal__header">
              <h2 id="milestone-master-edit-title" className="milestone-master-modal__title">
                Edit milestone
              </h2>
              <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => setEditModalTemplateId(null)}>
                Close
              </button>
            </div>
            <div className="milestone-master-modal__body">
              <TemplateEditorCard
                key={editModalTemplate.id}
                template={editModalTemplate}
                embedInModal
                onDeleted={() => setEditModalTemplateId(null)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
